import type { ChangeEvent, CompositionEvent, KeyboardEvent } from 'react';
import { toScanAscii } from '@/utils/scanAscii';
import { scannerInputAttributes } from './scannerInputAttributes';

/** Boxes whose scanner Enter landed while the IME was still composing, so the Enter is owed once it ends. */
const enterOwed = new WeakSet<HTMLInputElement>();

/** A form the browser submitted on its own has paid every Enter its boxes were owed; sending one more would scan twice. */
let submitWatched = false;
const watchSubmits = () => {
  if (submitWatched) {
    return;
  }
  submitWatched = true;
  document.addEventListener(
    'submit',
    (event) => {
      if (event.target instanceof HTMLFormElement) {
        for (const element of Array.from(event.target.elements)) {
          if (element instanceof HTMLInputElement) {
            enterOwed.delete(element);
          }
        }
      }
    },
    true,
  );
};

const isEnter = (event: KeyboardEvent<HTMLInputElement>): boolean =>
  event.key === 'Enter' || event.code === 'Enter' || event.code === 'NumpadEnter';

/** Chrome on Windows reports a composing key as keyCode 229 without setting isComposing. */
const isComposingKey = (event: KeyboardEvent<HTMLInputElement>): boolean =>
  event.nativeEvent.isComposing || event.keyCode === 229;

/**
 * Sends the Enter the IME swallowed: the box's own Enter handler runs first, and a form is submitted the
 * way the browser would have unless that handler prevented it.
 */
const resendEnter = (input: HTMLInputElement) => {
  const enter = new window.KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: true });
  if (input.dispatchEvent(enter) && input.form) {
    input.form.requestSubmit();
  }
};

/**
 * Enters a code into a scan box the way a keyboard-wedge scanner does: the box's value is replaced and its
 * own onChange runs, then — a turn later, once the stored value has re-rendered — the Enter the scanner
 * ends a code with is sent, so each screen's own lookup, validation and submit run unchanged. Used for
 * codes read by the camera, which never pass through the keyboard.
 */
export const typeScan = (input: HTMLInputElement, text: string, options: { enter?: boolean } = {}) => {
  const setValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  input.focus({ preventScroll: true });
  // The prototype setter, not `input.value =`, so React sees a change to report rather than its own write.
  setValue?.call(input, text);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  // A box whose Enter commits a write (a shelf for a put-away) takes the read only; the person confirms [SSD-7527].
  if (options.enter === false) {
    return;
  }
  window.setTimeout(() => {
    if (input.isConnected && !input.disabled) {
      resendEnter(input);
    }
  }, 0);
};

export interface AsciiScanFieldProps {
  autoComplete: 'off';
  autoCapitalize: 'off';
  spellCheck: false;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onCompositionEnd: (event: CompositionEvent<HTMLInputElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Everything a box a barcode is scanned into needs, so a scan reads the same whatever the OS keyboard's
 * Korean/English state [SSD-7050]. Hangul a QWERTY scan typed is mapped back to ASCII once the IME lets go
 * of the text — rewriting it mid-composition would fight the syllable the IME is still building — and an
 * Enter the IME swallowed while composing is sent again once composition ends, so the scan still submits.
 * Not a hook, so per-row boxes can use it too; `onValue` receives the text to store.
 */
export const asciiScanField = (
  onValue: (value: string) => void,
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void,
): AsciiScanFieldProps => ({
  ...scannerInputAttributes,
  onChange: (event) => {
    const raw = event.target.value;
    onValue((event.nativeEvent as InputEvent).isComposing ? raw : toScanAscii(raw));
  },
  onCompositionEnd: (event) => {
    const input = event.currentTarget;
    onValue(toScanAscii(input.value));
    if (enterOwed.has(input)) {
      // A turn later, so the stored value has re-rendered before the Enter reads it. Browsers that also
      // send a plain Enter after the composition settle it first, and this one then stands down.
      window.setTimeout(() => {
        if (enterOwed.delete(input)) {
          resendEnter(input);
        }
      }, 0);
    }
  },
  onKeyDown: (event) => {
    if (isEnter(event) && isComposingKey(event)) {
      // The field's own Enter handler waits for the composition to end rather than reading half a code.
      watchSubmits();
      enterOwed.add(event.currentTarget);
      return;
    }
    if (isEnter(event)) {
      enterOwed.delete(event.currentTarget);
    }
    onKeyDown?.(event);
  },
});
